import { Controller } from "@hotwired/stimulus";

// ── Turbo navigation: preserve body classes & suppress transitions ──
let turboListenersRegistered = false;

const NAV_ICON_SELECTOR = '[data-chat-drawer-target~="navIcon"]';

// The nav icon marks itself active with a PostCSS-module class name, which is
// only known to the server-rendered markup — hence `data-active-class`, with
// the unscoped name as the fallback.
function setNavIconsActive(icons, isActive) {
  icons.forEach((icon) => {
    const activeClass = icon.dataset.activeClass || "icon-link--active";
    activeClass
      .split(/\s+/)
      .filter(Boolean)
      .forEach((name) => icon.classList.toggle(name, isActive));
  });
}

function registerTurboListeners() {
  if (turboListenersRegistered) return;
  turboListenersRegistered = true;

  document.addEventListener("turbo:before-render", (event) => {
    // Save scroll positions of chat areas before they get detached
    const timeline = document.querySelector(".bryght-timeline");
    if (timeline) {
      sessionStorage.setItem("bryght_chat_temp_scroll", timeline.scrollTop);
      timeline.classList.add("turbo-navigating");
    }
    const roomList = document.querySelector(".bryght-room-list");
    if (roomList) {
      sessionStorage.setItem("bryght_chat_roomlist_scroll", roomList.scrollTop);
      roomList.classList.add("turbo-navigating");
    }

    const newBody = event.detail.newBody;
    newBody.classList.add("no-chat-transition");

    const chatOpen = sessionStorage.getItem("bryght_chat_open") === "true";

    if (chatOpen) {
      newBody.classList.add("chat-drawer-open");
      if (sessionStorage.getItem("bryght_chat_fullscreen") === "true") {
        newBody.classList.add("chat-drawer-fullscreen");
      }
    }

    // The sidebar is re-rendered by the server on every visit, and the server
    // cannot know the drawer is open: that state lives in sessionStorage, and
    // restoreState() strips the `?chat=open` param from the URL on arrival. So
    // the new icon always arrives inactive. Syncing it here — on the detached
    // body, before it is painted — is what keeps the icon from blinking off
    // and back on; leaving it to the controller's reconnect meant a rendered
    // frame with the wrong state. Both directions, because a cached snapshot
    // can just as well carry a stale active class into a closed drawer.
    setNavIconsActive(newBody.querySelectorAll(NAV_ICON_SELECTOR), chatOpen);
  });

  document.addEventListener("turbo:render", () => {
    // Restore scroll positions after the element is re-attached to the new body
    const timeline = document.querySelector(".bryght-timeline");
    if (timeline) {
      const savedScroll = sessionStorage.getItem("bryght_chat_temp_scroll");
      if (savedScroll !== null) {
        timeline.scrollTop = parseInt(savedScroll, 10);
        sessionStorage.removeItem("bryght_chat_temp_scroll");
      }
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          timeline.classList.remove("turbo-navigating");
        });
      });
    }

    const roomList = document.querySelector(".bryght-room-list");
    if (roomList) {
      const savedListScroll = sessionStorage.getItem("bryght_chat_roomlist_scroll");
      if (savedListScroll !== null) {
        roomList.scrollTop = parseInt(savedListScroll, 10);
        sessionStorage.removeItem("bryght_chat_roomlist_scroll");
      }
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          roomList.classList.remove("turbo-navigating");
        });
      });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.body.classList.remove("no-chat-transition");
      });
    });
  });
}

export default class extends Controller {
  static targets = ["drawer", "navIcon"];

  connect() {
    registerTurboListeners();

    window.addEventListener("popstate", this._handlePopState = this.handlePopState.bind(this));

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty("--chat-scrollbar-width", `${scrollbarWidth}px`);
    
    // Fallback: If the drawer is already here when connect runs (e.g., initial page load)
    if (this.hasDrawerTarget) {
      this.restoreState();
    }
  }

  disconnect() {
    if (this._handlePopState) {
      window.removeEventListener("popstate", this._handlePopState);
    }
  }

  /**
   * Called by Stimulus when the drawer target becomes available in the DOM.
   * This fires AFTER turbo-permanent moves the element into the new body,
   * solving the timing issue where connect() fires before targets are ready.
   */
  drawerTargetConnected(element) {
    // restoreState() loads the frame when the drawer is actually open; a closed
    // drawer leaves it untouched. The frame used to load on every page view so
    // the Matrix client could count unread messages — Rails counts them now
    // (BRY-928), and each of those loads cost a Matrix login (ADR 0008).
    this.restoreState();
  }

  handlePopState(event) {
    if (event && event.state && event.state.chat === "open") {
      sessionStorage.setItem("bryght_chat_open", "true");
      this.openDrawer();
    } else if (event && event.state && event.state.chat === "closed") {
      sessionStorage.removeItem("bryght_chat_open");
      this.closeDrawer();
    }
  }

  /**
   * Returns true when the chat is rendered inline as a route (mobile),
   * so the drawer logic should stay out of the way.
   */
  isChatRoute() {
    return document.body.classList.contains("chat-route-active");
  }

  restoreState() {
    // On the chat route (mobile), chat is inline – don't open the drawer.
    if (this.isChatRoute()) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("chat")) {
      sessionStorage.setItem("bryght_chat_open", "true");
      urlParams.delete("chat");
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? "?" + newSearch : "");
      history.replaceState({ chat: "open" }, "", newUrl);
      this.openDrawer();
      return;
    }

    const isOpen = sessionStorage.getItem("bryght_chat_open") === "true";
    if (isOpen) {
      history.replaceState({ chat: "open" }, "", window.location.href);
      this.openDrawer();
    } else {
      this.closeDrawer();
    }
  }

  toggle(event) {
    if (event) event.preventDefault();

    // On the chat route, don't toggle the drawer.
    if (this.isChatRoute()) {
      return;
    }

    const isOpen = sessionStorage.getItem("bryght_chat_open") === "true";

    if (isOpen) {
      sessionStorage.removeItem("bryght_chat_open");
      history.replaceState({ chat: "closed" }, "", window.location.href);
      this.closeDrawer();
    } else {
      sessionStorage.setItem("bryght_chat_open", "true");
      history.pushState({ chat: "open" }, "", window.location.href);
      this.openDrawer();
    }
  }

  /**
   * Loads the chat turbo-frame content if not already loaded.
   * Only ever called from openDrawer — opening the chat is what pays for a
   * Matrix session, nothing else.
   */
  ensureChatFrameLoaded() {
    if (!this.hasDrawerTarget) return;
    const frame = this.drawerTarget.querySelector("turbo-frame");

    if (frame && !frame.dataset.loaded) {
      frame.dataset.loaded = "true";
      frame.src = "/chat";

      frame.addEventListener("turbo:frame-load", () => {
        frame.removeAttribute("src");
      }, { once: true });
    }
  }

  openDrawer() {
    if (this.hasDrawerTarget) {
      this.drawerTarget.classList.add("is-open");

      if (sessionStorage.getItem("bryght_chat_fullscreen") === "true") {
        this.drawerTarget.classList.add("is-fullscreen");
        document.body.classList.add("chat-drawer-fullscreen");
      }

      // Ensure chat content is loaded
      this.ensureChatFrameLoaded();
    }

    setNavIconsActive(this.navIconTargets, true);

    document.body.classList.add("chat-drawer-open");
  }

  closeDrawer() {
    if (this.hasDrawerTarget) {
      this.drawerTarget.classList.remove("is-open");
      this.drawerTarget.classList.remove("is-fullscreen");
    }

    setNavIconsActive(this.navIconTargets, false);

    document.body.classList.remove("chat-drawer-open");
    document.body.classList.remove("chat-drawer-fullscreen");
  }

  toggleFullscreen(event) {
    if (event) event.preventDefault();
    if (this.hasDrawerTarget) {
      document.body.classList.add("no-chat-transition");

      const isFs = this.drawerTarget.classList.toggle("is-fullscreen");
      document.body.classList.toggle("chat-drawer-fullscreen", isFs);

      if (isFs) {
        sessionStorage.setItem("bryght_chat_fullscreen", "true");
      } else {
        sessionStorage.removeItem("bryght_chat_fullscreen");
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          document.body.classList.remove("no-chat-transition");
        });
      });
    }
  }
}
